import { FGSelectInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { MembershipHIstoryApi, SaveMembershipHistoryDto } from "../../../../../api";
import { FGSRSHDateMaskInput, SmallFormGenerator } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useSaveApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IPersonMembershipHistoryPanelProps {
  personId: number;
  onSaved: () => void;
}

export const PersonMembershipHistoryPanel: React.FunctionComponent<IPersonMembershipHistoryPanelProps> = ({
  personId,
  onSaved,
}) => {
  const { t } = useTl();
  const api = useApiService(MembershipHIstoryApi);
  const { currentLanguage } = useGlobalData();

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      membershipTypeId: Yup.number().nullable().required(t(ETLCodes.Required)),
      dateFrom: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("member-date", t(ETLCodes.MembershipDateExistsError), async function (value) {
          const error = await api.testMembershipHistoryExistance(this.parent.dateTo, value, personId);
          return !error.value;
        }),
      dateTo: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("member-date", t(ETLCodes.MembershipDateExistsError), async function (value) {
          const error = await api.testMembershipHistoryExistance(value, this.parent.dateFrom, personId);
          return !error.value;
        }),
    });
  }, [api, personId, t]);

  const [membershipTypes, mtloading] = useReferential((a) => a.getMembershipTypes(currentLanguage.languageId));

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: (data: SaveMembershipHistoryDto) => api.saveMembershipHistory(data),
        onSaveFn: () => onSaved(),
      }),
      [api, onSaved]
    )
  );

  const initialData = React.useMemo(
    () => new SaveMembershipHistoryDto({ personId: personId, membershipTypeId: 0 }).toJSON(),
    [personId]
  );

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      initialValues={initialData}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={150}
      saving={saving}
    >
      <FGSelectInput
        name="membershipTypeId"
        items={membershipTypes}
        loading={mtloading}
        label={t(ETLCodes.MembershipType)}
      />
      <FGSRSHDateMaskInput name="dateFrom" label={t(ETLCodes.DateFrom)} />
      <FGSRSHDateMaskInput name="dateTo" label={t(ETLCodes.DateTo)} />
    </SmallFormGenerator>
  );
};
